import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  Collapse,
  IconButton,
  TablePagination,
  CircularProgress,
  Alert,
  Tooltip,
  TextField,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AccessTime as TimeIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  WarningAmber as WarningAmberIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import AttendanceService from "../../Services/attendance.service.js";
import AssignmentService from "../../Services/assignment.service.js";
import MonthlyScheduleModal from "../Widgets/MonthlyScheduleModal/MonthlyScheduleModal";
import config from "../../config";
import selectedEnvironment from "../../env";

const BASEAPI = config[selectedEnvironment].API_URL;
const fmtMins = (m) => (m > 0 ? `${Math.floor(m / 60)}h ${(m % 60).toFixed(2)}m` : "-");

const formatTime = (dateTime) => {
    if (!dateTime) return "-";
    return dayjs(dateTime).format("HH:mm");
};

const formatDate = (date) => {
    return dayjs(date).format("DD/MM/YYYY");
};

const formatHours = (hours) => {
    if (hours === 0) return "-";
    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours - wholeHours) * 60);
    return `${wholeHours}:${minutes.toString().padStart(2, '0')}h`;
};

const stripHtmlTags = (html) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
};

export default function AttendanceView({
  accountantId,
  filters,
  token,
  direction,
  subAccountants,
}) {
  const { t } = useTranslation();

  /* ────────── state ────────── */
  const [records, setRecords] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [workMap, setWorkMap] = useState({}); // day → activeMinutes
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortOrder, setSortOrder] = useState('asc');
  const [sortField, setSortField] = useState('checkInTime');

  const getPairCount = (entries) => {
      if (!entries || !Array.isArray(entries)) return 0;
      let eventCount = 0;
      for (const entry of entries) {
          if (entry && entry.checkInTime) eventCount += 1;
          if (entry && entry.checkOutTime) eventCount += 1;
      }
      return Math.floor(eventCount / 2);
  };

  const getAttendanceIssues = (entries) => {
      if (!entries || !Array.isArray(entries)) return { missingCheckouts: 0, missingCheckins: 0, hasIssues: false };
      
      const checkInRecords = entries.filter(entry => entry.checkInTime && !entry.checkOutTime);
      const checkOutRecords = entries.filter(entry => entry.checkOutTime && !entry.checkInTime);
      
      const missingCheckouts = Math.max(0, checkInRecords.length - checkOutRecords.length);
      const missingCheckins = Math.max(0, checkOutRecords.length - checkInRecords.length);
      
      return {
          missingCheckouts,
          missingCheckins,
          hasIssues: missingCheckouts > 0 || missingCheckins > 0
      };
  };

  const calculateCorrectTotalHours = (entries) => {
      if (!entries || !Array.isArray(entries)) return 0;
      
      const checkInRecords = entries.filter(entry => entry.checkInTime && !entry.checkOutTime);
      const checkOutRecords = entries.filter(entry => entry.checkOutTime && !entry.checkInTime);
      
      const sortedCheckIns = checkInRecords.sort((a, b) => new Date(a.checkInTime) - new Date(b.checkInTime));
      const sortedCheckOuts = checkOutRecords.sort((a, b) => new Date(a.checkOutTime) - new Date(b.checkOutTime));
      
      let totalHours = 0;
      const minPairs = Math.min(sortedCheckIns.length, sortedCheckOuts.length);
      
      for (let i = 0; i < minPairs; i++) {
          const checkInTime = new Date(sortedCheckIns[i].checkInTime);
          const checkOutTime = new Date(sortedCheckOuts[i].checkOutTime);
          
          if (checkOutTime > checkInTime) {
              const diffMs = checkOutTime - checkInTime;
              const diffHours = diffMs / (1000 * 60 * 60);
              totalHours += diffHours;
          }
      }
      
      return totalHours;
  };

  const getPriorityEmoji = (priority) => {
      const priorityEmojis = {
          0: '🔴',
          1: '🟡',
          2: '🟢'
      };
      return priorityEmojis[priority] || '⚪';
  };

  /* ────────── 1. fetch attendance rows ────────── */
  const loadAttendanceData = async () => {
    try {
      const res = await AttendanceService.GetFilteredAttendance(
        accountantId,
        filters,
        token
      );
      const data = await res.json();
      setRecords(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load attendance", err);
    }
  };

  useEffect(() => {
    loadAttendanceData();
  }, [accountantId, filters, token]);

  /* ────────── 2. group by month → user → day → entries ────────── */
  const grouped = useMemo(() => {
    const map = {};
    records.forEach((r) => {
      const mKey = `${r.year}-${String(r.month).padStart(2, "0")}`;
      if (!map[mKey]) map[mKey] = {};
      if (!map[mKey][r.userId]) map[mKey][r.userId] = {};
      if (!map[mKey][r.userId][r.day]) map[mKey][r.userId][r.day] = [];
      map[mKey][r.userId][r.day].push(r);
    });
    return map;
  }, [records]);

  /* ────────── 3. all months in range (inclusive) ────────── */
  const monthsInRange = useMemo(() => {
    const months = [];
    let cur = dayjs(filters.startUtc).startOf("month");
    const end = dayjs(filters.endUtc).startOf("month");
    while (cur.isBefore(end) || cur.isSame(end)) {
      months.push(cur);
      cur = cur.add(1, "month");
    }
    return months;
  }, [filters]);

  /* ────────── 4. helpers to open / close modal ────────── */
  const handleRowClick = async (uid, mKey) => {
    const [year, month] = mKey.split('-');
    setSelectedUser(uid);
    setSelectedYear(parseInt(year));
    setSelectedMonth(parseInt(month));
    setOpenModal(true);
  };

  // Determine user type based on the user ID
  const getUserType = (userId) => {
    // Check if the user is in the subAccountants list
    const isSubAccountant = subAccountants.some(sa => sa.value === userId);
    return isSubAccountant ? 'SubAccountant' : 'Accountant';
  };
  
  const handleClose = () => {
    setOpenModal(false);
    setSelectedUser(null);
    setSelectedYear(null);
    setSelectedMonth(null);
  };

  /* ────────── 5. helpers for row expansion ────────── */
  const toggleRowExpansion = (day) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(day)) {
      newExpanded.delete(day);
    } else {
      newExpanded.add(day);
    }
    setExpandedRows(newExpanded);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const sortEntries = (entries) => {
    return [...entries].sort((a, b) => {
      const getEarliestTime = (entry) => {
        const checkIn = entry.checkInTime ? dayjs(entry.checkInTime) : null;
        const checkOut = entry.checkOutTime ? dayjs(entry.checkOutTime) : null;
        
        if (!checkIn && !checkOut) return dayjs(0);
        if (!checkIn) return checkOut;
        if (!checkOut) return checkIn;
        
        return checkIn.isBefore(checkOut) ? checkIn : checkOut;
      };
      
      const aTime = getEarliestTime(a);
      const bTime = getEarliestTime(b);
      
      if (sortOrder === 'asc') {
        return aTime.isBefore(bTime) ? -1 : aTime.isAfter(bTime) ? 1 : 0;
      } else {
        return aTime.isAfter(bTime) ? -1 : aTime.isBefore(bTime) ? 1 : 0;
      }
    });
  };

  /* ────────── 5. fetch Work‑in‑System when modal opens ────────── */
  useEffect(() => {
    if (!openModal || !selectedUser || !selectedMonth) {
      setWorkMap({});
      return;
    }

    (async () => {
      try {
        const start = `${selectedMonth}-01T00:00:00Z`;
        const endDay = dayjs(`${selectedMonth}-01`).daysInMonth();
        const end = `${selectedMonth}-${String(endDay).padStart(
          2,
          "0"
        )}T23:59:59Z`;

        const filterDto = {
          AccountantId: accountantId,
          UserIds: [selectedUser],
          CaseId: filters.clientCaseIds?.map(c => c.value)?.[0] || null,
          StartUtc: start,
          EndUtc: end,
        };

        const resp = await fetch(`${BASEAPI}/DailyMouseRecords/filter`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(filterDto),
        });
        const payload = await resp.json();
        if (!payload.success) throw new Error(payload.error);

        const map = {};
        payload.data.forEach((r) => {
          map[r.day] = (map[r.day] || 0) + r.activeMinutes;
        });
        setWorkMap(map);
      } catch (err) {
        console.error("Failed to load work‑in‑system", err);
        setWorkMap({});
      }
    })();
  }, [openModal, selectedUser, selectedMonth, accountantId, token]);

  /* ────────── render ────────── */
  return (
    <Box dir={direction} sx={{ px: 2, mt: 2 }}>
      {/* MONTH CARDS */}
      {monthsInRange.map((month) => {
        const key = month.format("YYYY-MM");
        const users = grouped[key] || {};
        const dayNumbers = Array.from(
          { length: month.daysInMonth() },
          (_, i) => i + 1
        );
        const hasAny = Object.values(users).some(
          (days) => Object.keys(days).length > 0
        );

        return (
          <Card key={key} variant="outlined" sx={{ 
            mb: 4, 
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
            border: '1px solid rgba(0, 0, 0, 0.05)',
            background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)'
          }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ 
                fontWeight: 700, 
                mb: 2,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                {t("Attendance")} – {month.format("MMMM YYYY")}
              </Typography>

              {!hasAny ? (
                <Typography
                  sx={{
                    py: 4,
                    textAlign: "center",
                    fontStyle: "italic",
                    opacity: 0.6,
                  }}
                >
                  {t("No entries for this month")}
                </Typography>
              ) : (
                <TableContainer component={Paper} sx={{ 
                  maxHeight: 600,
                  borderRadius: 2,
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
                  border: '1px solid rgba(0, 0, 0, 0.05)',
                  overflow: 'auto',
                  direction: direction === 'rtl' ? 'rtl' : 'ltr'
                }}>
                  <Table stickyHeader size="small" dir={direction} sx={{
                    '& th, & td': {
                      textAlign: direction === 'rtl' ? 'right' : 'left'
                    }
                  }}>
                    {/* HEADER */}
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                            textAlign: "center",
                            fontWeight: 700,
                            minWidth: 120,
                            border: 'none'
                          }}
                        >
                          {t("User")}
                        </TableCell>
                        {dayNumbers.map((d) => (
                          <TableCell
                            key={d}
                            sx={{
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              color: 'white',
                              textAlign: "center",
                              minWidth: 100,
                              border: 'none',
                              fontWeight: 600
                            }}
                          >
                            {d}
                          </TableCell>
                        ))}
                        <TableCell
                          sx={{
                            position: "sticky",
                            right: 0,
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                            textAlign: "center",
                            fontWeight: 700,
                            minWidth: 110,
                            zIndex: 2,
                            border: 'none'
                          }}
                        >
                          {t("Total")}
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    {/* BODY */}
                    <TableBody>
                      {Object.entries(users).map(([uid, days]) => {
                        /* sum total duration using pair logic for each day */
                        const attendMin = dayNumbers.reduce((sum, d) => {
                          const entries = days[d] || [];
                          if (entries.length === 0) return sum;
                          
                          // Use pair logic: separate check-ins and check-outs
                          const checkInRecords = entries.filter(entry => entry.checkInTime && !entry.checkOutTime);
                          const checkOutRecords = entries.filter(entry => entry.checkOutTime && !entry.checkInTime);
                          
                          const sortedCheckIns = checkInRecords.sort((a, b) => new Date(a.checkInTime) - new Date(b.checkInTime));
                          const sortedCheckOuts = checkOutRecords.sort((a, b) => new Date(a.checkOutTime) - new Date(b.checkOutTime));
                          
                          const minPairs = Math.min(sortedCheckIns.length, sortedCheckOuts.length);
                          
                          for (let i = 0; i < minPairs; i++) {
                            const checkInTime = new Date(sortedCheckIns[i].checkInTime);
                            const checkOutTime = new Date(sortedCheckOuts[i].checkOutTime);
                            
                            if (checkOutTime > checkInTime) {
                              const diffMs = checkOutTime - checkInTime;
                              const diffMinutes = diffMs / (1000 * 60);
                              sum += diffMinutes;
                            }
                          }
                          
                          return sum;
                        }, 0);

                        return (
                          <TableRow
                            key={uid}
                            hover
                            sx={{ 
                              cursor: "pointer",
                              '&:hover': {
                                backgroundColor: 'rgba(102, 126, 234, 0.04)'
                              }
                            }}
                            onClick={() => handleRowClick(uid, key)}
                          >
                            <TableCell sx={{ 
                              fontWeight: 700, 
                              textAlign: "center",
                              background: 'rgba(102, 126, 234, 0.05)'
                            }}>
                              {subAccountants.find((sa) => sa.value === uid)?.label ??
                                uid}
                            </TableCell>

                            {dayNumbers.map((d) => {
                              const entries = days[d] || [];
                              if (entries.length === 0) {
                                return (
                                  <TableCell key={d} sx={{ textAlign: "center" }}>
                                    <Typography variant="body2" sx={{ opacity: 0.3 }}>
                                      –
                                    </Typography>
                                  </TableCell>
                                );
                              }

                              // Show individual entries like in MonthlyScheduleModal
                              // Get check-in and check-out records separately
                              const checkInRecords = entries.filter(entry => entry.checkInTime && !entry.checkOutTime);
                              const checkOutRecords = entries.filter(entry => entry.checkOutTime && !entry.checkInTime);
                              
                              // Sort both arrays by time
                              const sortedCheckIns = checkInRecords.sort((a, b) => new Date(a.checkInTime) - new Date(b.checkInTime));
                              const sortedCheckOuts = checkOutRecords.sort((a, b) => new Date(a.checkOutTime) - new Date(b.checkOutTime));
                              
                              // Calculate total hours using pairing logic
                              let totalHours = 0;
                              const minPairs = Math.min(sortedCheckIns.length, sortedCheckOuts.length);
                              
                              for (let i = 0; i < minPairs; i++) {
                                const checkInTime = new Date(sortedCheckIns[i].checkInTime);
                                const checkOutTime = new Date(sortedCheckOuts[i].checkOutTime);
                                
                                if (checkOutTime > checkInTime) {
                                  const diffMs = checkOutTime - checkInTime;
                                  const diffHours = diffMs / (1000 * 60 * 60);
                                  totalHours += diffHours;
                                }
                              }

                              // Format the display like in MonthlyScheduleModal
                              const firstCheckIn = sortedCheckIns[0]?.checkInTime ? dayjs(sortedCheckIns[0].checkInTime).format("HH:mm") : "–";
                              const lastCheckOut = sortedCheckOuts[sortedCheckOuts.length - 1]?.checkOutTime ? dayjs(sortedCheckOuts[sortedCheckOuts.length - 1].checkOutTime).format("HH:mm") : "–";
                              
                              const totalHoursFormatted = totalHours > 0 ? `(${Math.floor(totalHours)}h ${Math.round((totalHours - Math.floor(totalHours)) * 60)}m)` : "";
                              const entryCount = Math.max(sortedCheckIns.length, sortedCheckOuts.length);

                              return (
                                <TableCell key={d} sx={{ textAlign: "center" }}>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 500, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' }}>
                                      {`${firstCheckIn} - ${lastCheckOut}`}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                                      {totalHoursFormatted}
                                    </Typography>
                                    {entryCount > 1 && (
                                      <Chip
                                        label={`${entryCount}+`}
                                        size="small"
                                        color="primary"
                                        sx={{ fontSize: '0.7rem', height: 20 }}
                                      />
                                    )}
                                  </Box>
                                </TableCell>
                              );
                            })}

                            <TableCell
                              sx={{
                                position: "sticky",
                                right: 0,
                                background: 'rgba(102, 126, 234, 0.08)',
                                fontWeight: 700,
                                textAlign: "center",
                                zIndex: 1,
                                border: '1px solid rgba(102, 126, 234, 0.1)'
                              }}
                            >
                              <Chip
                                label={fmtMins(attendMin)}
                                color="primary"
                                variant="outlined"
                                size="small"
                                sx={{ fontWeight: 600 }}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        );
      })}

      {/* ────────── MONTHLY SCHEDULE MODAL ────────── */}
      <MonthlyScheduleModal
        open={openModal}
        onClose={handleClose}
        authToken={token}
        year={selectedYear}
        month={selectedMonth}
        userId={selectedUser}
        userType={selectedUser ? getUserType(selectedUser) : null}
        onDataChange={loadAttendanceData}
      />
    </Box>
  );
}
